<script setup lang="ts">
import { computed, ref, inject, watch } from 'vue';
import type { Ref } from 'vue';
import { DtItemLayout } from '@dialpad/dialtone/vue3/lib/item-layout';
import { DtIllustration } from '@dialpad/dialtone/vue3/lib/illustration';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { DtBadge } from '@dialpad/dialtone/vue3/lib/badge';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtValidationMessages } from '@dialpad/dialtone/vue3/lib/validation-messages';
import { DtLink } from '@dialpad/dialtone/vue3/lib/link';
import { DtTooltip } from '@dialpad/dialtone/vue3/lib/tooltip';

import { validateFile, validateHeaders } from '@/csv/utils/csv-file-parser';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import { useI18N } from '@dialpad/i18n';
import { DATA_QA, DNC_TEMPLATE_DATA } from './dnc-csv-upload.constants';
import logger from '@/logger';
import { downloadBlob } from '@/csv/utils/csv-generate-download-blob';
import { arrayToCsv } from '@/csv/utils/csv-array-to-csv';
import { validateE164PhoneNumber } from '@/dnc/utils/dnc-utils';

const log = logger.getChild('DncCsvUpload');

const { $t, $ta } = useI18N();

enum DncCsvUploadClientError {
  REQUIRED = 'REQUIRED',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  PARSING_ERROR = 'PARSING_ERROR',
  DUPLICATE_HEADERS = 'DUPLICATE_HEADERS',
  NO_PHONE_COLUMN = 'NO_PHONE_COLUMN',
  INVALID_PHONE_FORMAT = 'INVALID_PHONE_FORMAT',
  UNKNOWN = 'UNKNOWN',
}

type DncCsvUploadError = DncCsvUploadClientError | null;

const model = defineModel<File | null>({
  default: null,
});

const props = defineProps<{
  required?: boolean;
  apiError?: string | null;
  processing?: boolean;
}>();

const validationTrigger = inject<Ref<number>>(TRIGGER_FORM_VALIDATION, ref(0));

watch([validationTrigger], () => {
  if (props.required && !file.value) {
    setError(DncCsvUploadClientError.REQUIRED);
  }
});

const emit = defineEmits({
  'update:examples': (examples: string[]) => true,
  'update:error': (error: DncCsvUploadError) => true,
});

const isDragging = ref(false);
const file = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const error = ref<DncCsvUploadError>(null);
const headers = ref<string[]>([]);

const setError = (newError: DncCsvUploadError) => {
  log.debug('Setting error:', newError);
  const errorMessages = $ta('DNC_CSV_UPLOAD_ERROR');
  if (newError === null) {
    error.value = null;
    return;
  }

  if (!errorMessages[newError]) {
    log.error('Unknown error:', newError);
    error.value = DncCsvUploadClientError.UNKNOWN;
    return;
  }

  error.value = newError;
};

defineExpose({ setError });

watch(error, (newError) => {
  emit('update:error', newError);
});

const setFile = (newFile: File | null) => {
  file.value = newFile;
  model.value = newFile;
};

const setData = (data: [string[], string[]]) => {
  headers.value = data[0];
  emit('update:examples', data[1]);
};

const clearFile = () => {
  setFile(null);
  setData([[], []]);
  setError(null);
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const isPhoneExample = (example: string): boolean => {
  if (!example) return false;
  return (
    validateE164PhoneNumber(example) ||
    /^\d{10,15}$/.test(example.replace(/\D/g, ''))
  );
};

const validateDncCsvData = (headers: string[], examples: string[]): boolean => {
  const hasPhoneColumn = examples.some((example) => isPhoneExample(example));

  if (!hasPhoneColumn) {
    setError(DncCsvUploadClientError.NO_PHONE_COLUMN);
    return false;
  }

  return true;
};

const validateDncFile = (inputFile: File): boolean => {
  return validateFile(inputFile);
};

const processFile = async (inputFile: File) => {
  if (validateDncFile(inputFile)) {
    try {
      const data = await validateHeaders(inputFile);

      if (!validateDncCsvData(data[0], data[1])) {
        return false;
      }

      setData(data);
      setError(null);
      log.log('DNC file uploaded:', inputFile.name);
      return true;
    } catch {
      setError(DncCsvUploadClientError.PARSING_ERROR);
      log.log('Error parsing DNC file:', inputFile.name);
      return false;
    }
  } else {
    setError(DncCsvUploadClientError.INVALID_FILE_TYPE);
    return false;
  }
};

const handleFile = async (inputFile?: File) => {
  if (!inputFile) return;

  file.value = null;
  const success = await processFile(inputFile);
  if (success) {
    setFile(inputFile);
  }
};

watch(
  () => model.value,
  async (newFile) => {
    if (newFile) {
      file.value = null;
      const success = await processFile(newFile);
      if (success) {
        file.value = newFile;
      }
    } else {
      clearFile();
    }
  },
  { immediate: true },
);

const handleDragOver = () => {
  isDragging.value = true;
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const handleDrop = async (event: DragEvent) => {
  isDragging.value = false;

  const droppedFile = event.dataTransfer?.files[0];
  if (!droppedFile) {
    return;
  }
  await handleFile(droppedFile);
};

const onFile = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const selectedFile = target.files?.[0];
  await handleFile(selectedFile);
};

const containerClasses = computed(() => {
  return {
    'd-bc-default': !error.value && !isDragging.value && !props.processing,
    'd-bc-critical': error.value && !isDragging.value,
    'd-bc-focus': isDragging.value,
    'd-bgc-info-subtle': isDragging.value,
    'd-bas-dashed d-bc-default':
      !file.value && !error.value && !props.processing,
    'd-bgc-critical-subtle': error.value,
  };
});

const openFileDialog = () => {
  log.debug('Opening file dialog');
  fileInput.value?.click();
};

const csv = arrayToCsv(DNC_TEMPLATE_DATA);
</script>
<template>
  <div class="dnc-csv-upload" :data-qa="DATA_QA.ROOT">
    <DtItemLayout
      :class="containerClasses"
      class="d-d-flex d-pl16 d-pr24 d-py16 d-ba d-bar16 d-baw1 d-hmn64 d-g16"
      :data-qa="DATA_QA.CONTAINER"
      @dragover.prevent="handleDragOver"
      @dragleave="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <template #left>
        <div class="d-d-flex d-ai-center d-jc-center d-h64 d-w64">
          <template v-if="props.processing">
            <DtIcon
              name="loading"
              size="500"
              class="dnc-csv-upload__loading d-fc-muted"
            />
          </template>
          <template v-else-if="file">
            <div
              class="d-d-flex d-ai-center d-jc-center d-h64 d-w64 d-bgc-secondary d-bar8"
            >
              <DtIcon name="google-sheets" size="500" />
            </div>
          </template>
          <template v-else>
            <DtIllustration name="file-upload" />
          </template>
        </div>
      </template>
      <label class="d-d-none">
        {{ $t('DNC_CSV_UPLOAD_INPUT_LABEL') }}
        <input
          ref="fileInput"
          class="d-vi-hidden"
          type="file"
          accept=".csv"
          :data-qa="DATA_QA.FILE_INPUT"
          @change="onFile"
        />
      </label>
      <div>
        <p v-if="props.processing" class="d-mb4">
          {{ $t('DNC_CSV_UPLOAD_PROCESSING') }}
        </p>
        <p
          v-else-if="file"
          :data-qa="DATA_QA.FILE_NAME"
          class="file-name d-ww-break-word"
        >
          {{ file.name }}
        </p>
        <p v-else class="d-mb4">
          {{ $t('DNC_CSV_UPLOAD_LABEL_TEXT') }}
          <DtLink
            href="#"
            :aria-label="$t('DNC_CSV_UPLOAD_UPLOAD_ARIA_LABEL')"
            class=""
            :data-qa="DATA_QA.UPLOAD_LINK"
            @click.prevent="openFileDialog"
          >
            {{ $t('DNC_CSV_UPLOAD_UPLOAD_TEXT') }}
          </DtLink>
          {{ $t('DNC_CSV_UPLOAD_LABEL_SUFFIX') }}
        </p>
      </div>
      <template v-if="!file && !props.processing" #subtitle>
        {{ $t('DNC_CSV_UPLOAD_SUBTITLE') }}
      </template>
      <template v-if="file" #bottom>
        <div :data-qa="DATA_QA.HEADERS">
          <DtBadge v-for="header in headers" :key="header" class="d-mr4 d-my2">
            {{ header }}
          </DtBadge>
        </div>
      </template>
      <template v-if="file" #right>
        <DtTooltip placement="top">
          {{ $t('DNC_CSV_UPLOAD_NEW_FILE_TOOLTIP') }}
          <template #anchor>
            <DtButton
              importance="clear"
              kind="muted"
              class="d-mr2"
              :data-qa="DATA_QA.UPLOAD_ANOTHER"
              @click="openFileDialog"
            >
              <template #icon>
                <DtIcon name="upload" size="400" />
              </template>
            </DtButton>
          </template>
        </DtTooltip>
        <DtTooltip placement="top">
          {{ $t('DNC_CSV_UPLOAD_REMOVE_TOOLTIP') }}
          <template #anchor>
            <DtButton
              importance="clear"
              kind="muted"
              :data-qa="DATA_QA.CLEAR"
              @click="clearFile"
            >
              <template #icon>
                <DtIcon name="trash" size="400" />
              </template>
            </DtButton>
          </template>
        </DtTooltip>
      </template>
    </DtItemLayout>
    <DtValidationMessages
      v-if="error"
      :data-qa="DATA_QA.ERROR_MESSAGE"
      :validation-messages="[
        { message: $ta(`DNC_CSV_UPLOAD_ERROR`)[error], type: 'error' },
      ]"
    />
    <p class="d-mt16 d-mb4 d-fs-100 d-fc-tertiary">
      {{ $t('DNC_CSV_UPLOAD_TITLE') }}
    </p>
    <ul class="d-ml16 d-fs-100 d-fc-tertiary">
      <li class="d-lst-disc">{{ $t('DNC_CSV_UPLOAD_RULE_1') }}</li>
      <li class="d-lst-disc">{{ $t('DNC_CSV_UPLOAD_RULE_2') }}</li>
      <li class="d-lst-disc">{{ $t('DNC_CSV_UPLOAD_RULE_3') }}</li>
      <li class="d-lst-disc">
        <DtLink
          href="#"
          :data-qa="DATA_QA.DOWNLOAD_TEMPLATE"
          @click="
            downloadBlob(csv, 'dnc-template.csv', 'text/csv;charset=utf-8;')
          "
        >
          {{ $t('DNC_CSV_UPLOAD_DOWNLOAD_TEMPLATE') }}
        </DtLink>
      </li>
    </ul>
  </div>
</template>
<style type="less">
.dnc-csv-upload .d-item-layout__left {
  min-width: var(--dt-size-700) !important;
  width: var(--dt-size-700) !important;
  height: var(--dt-size-700) !important;
  padding-right: var(--dt-space-0) !important;
  justify-content: center !important;
  align-self: center !important;
}

.dnc-csv-upload__loading {
  animation: d-loading-circle 900ms infinite linear;
}
</style>
